import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, Button, Select, MenuItem, FormControl, InputLabel, TextField } from '@mui/material';
import KpiCard from '../ui/KpiCard';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const backendUrl = 'http://localhost:3001/api';

const NonPerformingLoans: React.FC = () => {
  const [bucket, setBucket] = useState<30 | 60 | 90>(30);
  const [merchantId, setMerchantId] = useState('');
  const [data, setData] = useState<any>(null);
  const [allBuckets, setAllBuckets] = useState<any[]>([]);

  useEffect(() => {
    const params = { bucket, merchantId: merchantId || undefined };
    axios.get(`${backendUrl}/metrics/nonperforming`, { params }).then(r => setData(r.data));
    
    // Fetch all bucket counts for chart
    Promise.all([
      axios.get(`${backendUrl}/metrics/nonperforming`, { params: { bucket: 30, merchantId: merchantId || undefined } }),
      axios.get(`${backendUrl}/metrics/nonperforming`, { params: { bucket: 60, merchantId: merchantId || undefined } }),
      axios.get(`${backendUrl}/metrics/nonperforming`, { params: { bucket: 90, merchantId: merchantId || undefined } })
    ]).then(([r30, r60, r90]) => {
      setAllBuckets([
        { bucket: '30+ days', count: r30.data.count },
        { bucket: '60+ days', count: r60.data.count },
        { bucket: '90+ days', count: r90.data.count }
      ]);
    });
  }, [bucket, merchantId]);

  const exportToCsv = () => {
    if (!data?.loans) return;
    const csv = [
      'ID,Applicant Name,Last Payment Date,Days Overdue',
      ...data.loans.map((loan: any) => {
        const daysDiff = Math.floor((new Date().getTime() - new Date(loan.lastPaymentDate).getTime()) / (1000*60*60*24));
        return `${loan.id},${loan.applicantName},${loan.lastPaymentDate},${daysDiff}`;
      })
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `np-loans-${bucket}days.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <h2 style={{ fontWeight: 800, marginBottom: 24 }}>Non-Performing Loans</h2>
      
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'center' }}>
        <FormControl size="small" style={{ minWidth: 120 }}>
          <InputLabel>Days Bucket</InputLabel>
          <Select value={bucket} label="Days Bucket" onChange={(e) => setBucket(e.target.value as any)}>
            <MenuItem value={30}>30+ days</MenuItem>
            <MenuItem value={60}>60+ days</MenuItem>
            <MenuItem value={90}>90+ days</MenuItem>
          </Select>
        </FormControl>
        <TextField 
          size="small" 
          placeholder="Merchant ID (optional)" 
          value={merchantId} 
          onChange={(e) => setMerchantId(e.target.value)}
          style={{ minWidth: 200 }}
        />
        <Button variant="contained" onClick={exportToCsv} disabled={!data?.loans?.length}>
          Export CSV
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <KpiCard title={`${bucket}+ Days Overdue`} value={data ? data.count : '—'} color="#ff4d4f" />
        <div style={{ flex: '1 1 400px' }}>
          <Card style={{ 
            padding: 24, 
            height: '100%',
            background: 'linear-gradient(135deg, #ffffff 0%, #fef7f0 100%)',
            border: '1px solid #fed7aa',
            boxShadow: '0 4px 20px rgba(251, 146, 60, 0.1)',
            borderRadius: 16
          }}>
            <h4 style={{ 
              marginTop: 0, 
              fontSize: 18, 
              fontWeight: 700, 
              color: '#1e293b',
              marginBottom: 16
            }}>NP Buckets Distribution</h4>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={allBuckets} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="npGradient30" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.9}/>
                      <stop offset="95%" stopColor="#dc2626" stopOpacity={0.7}/>
                    </linearGradient>
                    <linearGradient id="npGradient60" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.9}/>
                      <stop offset="95%" stopColor="#ea580c" stopOpacity={0.7}/>
                    </linearGradient>
                    <linearGradient id="npGradient90" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.9}/>
                      <stop offset="95%" stopColor="#d97706" stopOpacity={0.7}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="bucket" 
                    tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#ffffff', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: 12,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                    }}
                    labelStyle={{ fontWeight: 600, color: '#1e293b' }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="url(#npGradient30)"
                    radius={[4, 4, 0, 0]}
                    name="Non-Performing Loans"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>

      <Card style={{ padding: 16 }}>
        <h4 style={{ marginTop: 0 }}>Loan Details</h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e0e7ff' }}>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Loan ID</th>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Applicant</th>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Last Payment</th>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Days Overdue</th>
              </tr>
            </thead>
            <tbody>
              {data?.loans?.map((loan: any) => {
                const daysDiff = Math.floor((new Date().getTime() - new Date(loan.lastPaymentDate).getTime()) / (1000*60*60*24));
                return (
                  <tr key={loan.id} style={{ borderBottom: '1px solid #f0f4f8' }}>
                    <td style={{ padding: 12, fontFamily: 'monospace' }}>{loan.id.slice(0, 8)}...</td>
                    <td style={{ padding: 12 }}>{loan.applicantName}</td>
                    <td style={{ padding: 12 }}>{new Date(loan.lastPaymentDate).toLocaleDateString()}</td>
                    <td style={{ padding: 12, color: '#ff4d4f', fontWeight: 600 }}>{daysDiff} days</td>
                  </tr>
                );
              }) || []}
            </tbody>
          </table>
          {(!data?.loans || data.loans.length === 0) && (
            <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>
              No non-performing loans found for this bucket.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default NonPerformingLoans;
